import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Target, Plus, DollarSign, Calendar, User, Home, Building2,
  ChevronLeft, ChevronRight, ArrowRight, Loader2, GripVertical,
  MoreVertical, Edit2, Trash2, CheckCircle, XCircle, TrendingUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STAGES = [
  { id: 'prospecto', name: 'Prospecto', probability: 10, color: '#6B7280' },
  { id: 'contacto_inicial', name: 'Contacto Inicial', probability: 20, color: '#3B82F6' },
  { id: 'calificado', name: 'Calificado', probability: 30, color: '#8B5CF6' },
  { id: 'visita_programada', name: 'Visita Programada', probability: 40, color: '#F59E0B' },
  { id: 'visita_realizada', name: 'Visita Realizada', probability: 50, color: '#10B981' },
  { id: 'negociacion', name: 'Negociación', probability: 60, color: '#EC4899' },
  { id: 'propuesta_enviada', name: 'Propuesta Enviada', probability: 70, color: '#6366F1' },
  { id: 'documentacion', name: 'Documentación', probability: 80, color: '#14B8A6' },
  { id: 'cierre_pendiente', name: 'Cierre Pendiente', probability: 90, color: '#F97316' },
];

const OPERATION_TYPES = {
  venta: { name: 'Venta', color: 'bg-green-100 text-green-700' },
  renta: { name: 'Renta', color: 'bg-blue-100 text-blue-700' },
  traspaso: { name: 'Traspaso', color: 'bg-purple-100 text-purple-700' },
};

export default function CRMPipelinePage() {
  const [pipeline, setPipeline] = useState({});
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [selectedOpp, setSelectedOpp] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    operation_type: 'venta',
    contact_id: '',
    property_id: '',
    amount: '',
    stage: 'prospecto',
    close_date: '',
    description: '',
    lead_source: 'website'
  });

  const token = localStorage.getItem('upim_token');
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [pipelineRes, contactsRes, propertiesRes] = await Promise.all([
        axios.get(`${API_URL}/api/crm/opportunities/pipeline`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/crm/contacts?limit=100`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/crm/properties?limit=100`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setPipeline(pipelineRes.data.pipeline || {});
      setSummary(pipelineRes.data.summary || null);
      setContacts(contactsRes.data.contacts || []);
      setProperties(propertiesRes.data.properties || []);
    } catch (err) {
      console.error('Error loading pipeline:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedOpp(null);
    setFormData({
      name: '',
      operation_type: 'venta',
      contact_id: '',
      property_id: '',
      amount: '',
      stage: 'prospecto',
      close_date: '',
      description: '',
      lead_source: 'website'
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const payload = {
        ...formData,
        amount: formData.amount ? parseFloat(formData.amount) : null,
        close_date: formData.close_date ? new Date(formData.close_date).toISOString() : null,
        contact_id: formData.contact_id || null,
        property_id: formData.property_id || null,
      };

      await axios.post(`${API_URL}/api/crm/opportunities`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowModal(false);
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al crear oportunidad');
    } finally {
      setSaving(false);
    }
  };

  const moveOpportunity = async (oppId, newStage) => {
    try {
      await axios.patch(`${API_URL}/api/crm/opportunities/${oppId}/stage`, 
        { stage: newStage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadData();
    } catch (err) {
      alert('Error al mover oportunidad');
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      maximumFractionDigits: 0
    }).format(value);
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white">
          <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-center gap-4">
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="text-white hover:bg-white/10"
                  onClick={() => navigate('/crm')}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  CRM
                </Button>
                <div>
                  <h1 className="text-2xl lg:text-3xl font-bold flex items-center gap-3">
                    <Target className="h-8 w-8" />
                    Pipeline de Ventas
                  </h1>
                  <p className="text-purple-100 mt-1">Gestiona tus oportunidades de negocio</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Button 
                  className="bg-white text-purple-600 hover:bg-purple-50"
                  onClick={handleCreate}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Nueva Oportunidad
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        {summary && (
          <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                      <Target className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{summary.total_opportunities}</p>
                      <p className="text-sm text-gray-500">Oportunidades</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                      <DollarSign className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xl font-bold">{formatCurrency(summary.total_value)}</p>
                      <p className="text-sm text-gray-500">Valor total</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <TrendingUp className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xl font-bold">{formatCurrency(summary.weighted_value)}</p>
                      <p className="text-sm text-gray-500">Valor ponderado</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
                      <Calendar className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">
                        {Object.values(pipeline).reduce((acc, stage) => 
                          acc + stage.opportunities.filter(o => {
                            const closeDate = new Date(o.close_date);
                            const now = new Date();
                            const weekFromNow = new Date();
                            weekFromNow.setDate(weekFromNow.getDate() + 7);
                            return closeDate >= now && closeDate <= weekFromNow;
                          }).length
                        , 0)}
                      </p>
                      <p className="text-sm text-gray-500">Esta semana</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Pipeline Board */}
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          <div className="overflow-x-auto">
            <div className="flex gap-4 min-w-max pb-4">
              {STAGES.map(stage => {
                const stageData = pipeline[stage.id] || { opportunities: [], total_amount: 0, count: 0 };
                
                return (
                  <div
                    key={stage.id}
                    className="w-72 flex-shrink-0"
                  >
                    {/* Stage Header */}
                    <div 
                      className="p-3 rounded-t-xl text-white"
                      style={{ backgroundColor: stage.color }}
                    >
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold">{stage.name}</h3>
                        <Badge className="bg-white/20 text-white">
                          {stageData.count || 0}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between mt-1 text-sm text-white/80">
                        <span>{stage.probability}% probabilidad</span>
                        <span>{formatCurrency(stageData.total_amount)}</span>
                      </div>
                    </div>
                    
                    {/* Stage Cards */}
                    <div className="bg-gray-100 rounded-b-xl p-2 min-h-[400px] space-y-2">
                      {stageData.opportunities?.length === 0 ? (
                        <div className="text-center py-8 text-gray-400">
                          <Target className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">Sin oportunidades</p>
                        </div>
                      ) : (
                        stageData.opportunities?.map(opp => (
                          <Card
                            key={opp.id}
                            className="cursor-pointer hover:shadow-md transition-shadow"
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.setData('oppId', opp.id);
                              e.dataTransfer.setData('fromStage', stage.id);
                            }}
                          >
                            <CardContent className="p-3">
                              <div className="flex items-start justify-between mb-2">
                                <h4 className="font-medium text-gray-900 text-sm line-clamp-2">
                                  {opp.name}
                                </h4>
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                                  <MoreVertical className="h-3 w-3" />
                                </Button>
                              </div>
                              
                              <p className="text-lg font-bold text-gray-900 mb-2">
                                {formatCurrency(opp.amount)}
                              </p>
                              
                              {opp.close_date && (
                                <p className="text-xs text-gray-500 flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  {new Date(opp.close_date).toLocaleDateString('es-MX')}
                                </p>
                              )}
                              
                              {opp.ai_score && (
                                <div className="mt-2">
                                  <div className="flex items-center gap-1 text-xs text-gray-500">
                                    <span>IA Score:</span>
                                    <div className="flex-1 h-1.5 bg-gray-200 rounded-full">
                                      <div 
                                        className="h-full rounded-full bg-gradient-to-r from-amber-400 to-green-500"
                                        style={{ width: `${opp.ai_score}%` }}
                                      />
                                    </div>
                                    <span className="font-medium">{opp.ai_score}</span>
                                  </div>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        ))
                      )}
                      
                      {/* Drop zone indicator */}
                      <div
                        className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center text-gray-400 text-sm opacity-0 hover:opacity-100 transition-opacity"
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.currentTarget.classList.add('bg-purple-50', 'border-purple-300');
                        }}
                        onDragLeave={(e) => {
                          e.currentTarget.classList.remove('bg-purple-50', 'border-purple-300');
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.currentTarget.classList.remove('bg-purple-50', 'border-purple-300');
                          const oppId = e.dataTransfer.getData('oppId');
                          const fromStage = e.dataTransfer.getData('fromStage');
                          if (fromStage !== stage.id) {
                            moveOpportunity(oppId, stage.id);
                          }
                        }}
                      >
                        Soltar aquí
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {/* Closed columns */}
              <div className="w-72 flex-shrink-0">
                <div className="p-3 rounded-t-xl bg-green-600 text-white">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5" />
                    <h3 className="font-semibold">Cerrado Ganado</h3>
                  </div>
                </div>
                <div className="bg-green-50 rounded-b-xl p-2 min-h-[200px] text-center py-8 text-green-600">
                  <CheckCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Oportunidades ganadas</p>
                </div>
              </div>
              
              <div className="w-72 flex-shrink-0">
                <div className="p-3 rounded-t-xl bg-red-600 text-white">
                  <div className="flex items-center gap-2">
                    <XCircle className="h-5 w-5" />
                    <h3 className="font-semibold">Cerrado Perdido</h3>
                  </div>
                </div>
                <div className="bg-red-50 rounded-b-xl p-2 min-h-[200px] text-center py-8 text-red-600">
                  <XCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Oportunidades perdidas</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white">
              <h2 className="text-xl font-bold text-gray-900">Nueva Oportunidad</h2>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre de la oportunidad *
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ej: Venta Casa Polanco - García"
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de operación</label>
                  <select
                    value={formData.operation_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, operation_type: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    {Object.entries(OPERATION_TYPES).map(([key, val]) => (
                      <option key={key} value={key}>{val.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Etapa inicial</label>
                  <select
                    value={formData.stage}
                    onChange={(e) => setFormData(prev => ({ ...prev, stage: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    {STAGES.map(stage => (
                      <option key={stage.id} value={stage.id}>{stage.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Monto estimado</label>
                  <Input
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                    placeholder="$0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fecha estimada de cierre</label>
                  <Input
                    type="date"
                    value={formData.close_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, close_date: e.target.value }))}
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contacto relacionado</label>
                <select
                  value={formData.contact_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_id: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Sin contacto asignado</option>
                  {contacts.map(c => (
                    <option key={c.id} value={c.id}>{c.full_name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Propiedad relacionada</label>
                <select
                  value={formData.property_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, property_id: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Sin propiedad asignada</option>
                  {properties.map(p => (
                    <option key={p.id} value={p.id}>{p.name} - {formatCurrency(p.price)}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Detalles adicionales de la oportunidad..."
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setShowModal(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1" disabled={saving}>
                  {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Crear Oportunidad
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
